import * as React from 'react'
import { ExternalLink } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { COUPLE_FEEDBACK_BODY_MAX } from '@lib/constants/couple-feedback'
import type { CoupleFeedbackPageDto, VenueReviewLinkDto } from '@lib/dto/couple-feedback.dto'
import type { CoupleFeedbackSentiment } from '@lib/enums/couple-feedback-sentiment.enum'
import { submitCoupleFeedbackInputSchema } from '@lib/input/couple-feedback.input'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { useCoupleFeedbackClick, useCoupleFeedbackPage, useSubmitCoupleFeedback } from '@/hooks/useCoupleFeedback'
import { instantLabel } from '@/lib/instantLabel'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ChoiceCard } from '@/components/ui/choice-card'
import { Field } from '@/components/ui/field'
import { FormError } from '@/components/ui/form-error'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { Textarea } from '@/components/ui/textarea'
import { SENTIMENT_TITLE } from '@/lib/couple-feedback-labels'
import { publicReviewEmphasis, reviewLinkButtons, type PublicReviewEmphasis } from './couple-feedback-links'

/**
 * Every review place the venue offers, in its order, for either answer. The
 * tap is recorded without waiting, so the browser keeps the click and opens
 * the link.
 */
function PublicReviews({
  eventId,
  links,
  emphasis,
}: {
  eventId: string
  links: readonly VenueReviewLinkDto[]
  emphasis: PublicReviewEmphasis
}) {
  const click = useCoupleFeedbackClick(eventId)
  const buttons = reviewLinkButtons(links)
  if (buttons.length === 0) return null
  const heading = emphasis === 'next' ? 'If you’d like, say it publicly' : null
  return (
    <div className="space-y-2">
      {heading !== null ? <p className="text-sm font-medium text-ink">{heading}</p> : null}
      <div className="flex flex-wrap gap-2">
        {buttons.map((button) => (
          <Button key={button.url} variant="outline" asChild>
            <a
              href={button.url}
              target="_blank"
              rel="noreferrer"
              onClick={() => click.mutate({ url: button.url })}
            >
              {button.label}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </a>
          </Button>
        ))}
      </div>
    </div>
  )
}

/** What this person already sent. There is no way to change it. */
function SentAnswer({ eventId, page }: { eventId: string; page: CoupleFeedbackPageDto }) {
  const answer = page.answer
  if (answer === null) return null
  const note = answer.body !== null && answer.body.length > 0
  const emphasis = publicReviewEmphasis(answer.sentiment)
  return (
    <div className="space-y-6">
      <Card className="min-w-0 space-y-2 wrap-anywhere p-4">
        <p className="text-heading font-semibold text-ink">{SENTIMENT_TITLE[answer.sentiment]}</p>
        {note ? <p className="m-0 min-w-0 whitespace-pre-wrap text-sm">{answer.body}</p> : null}
        <p className="m-0 text-small text-ink-muted">Sent {instantLabel(answer.sentAt)}. This can’t be changed.</p>
      </Card>
      {emphasis !== null ? <PublicReviews eventId={eventId} links={page.reviewLinks} emphasis={emphasis} /> : null}
    </div>
  )
}

/** The two choices and the note. Public links wait until the answer is sent. */
function AskForm({ eventId, page }: { eventId: string; page: CoupleFeedbackPageDto }) {
  const submit = useSubmitCoupleFeedback(eventId)
  const [sentiment, setSentiment] = React.useState<CoupleFeedbackSentiment | null>(null)
  const [body, setBody] = React.useState('')
  const [bodyError, setBodyError] = React.useState<string | null>(null)
  const noteRequired = sentiment === 'needs_attention'

  function choose(next: CoupleFeedbackSentiment): void {
    setSentiment(next)
    setBodyError(null)
  }

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault()
    if (sentiment === null) return
    const trimmed = body.trim()
    const parsed = submitCoupleFeedbackInputSchema.safeParse({
      sentiment,
      ...(trimmed.length > 0 ? { body: trimmed } : {}),
    })
    if (!parsed.success) {
      setBodyError(parsed.error.issues.find((issue) => issue.path[0] === 'body')?.message ?? 'Say what needs attention.')
      return
    }
    setBodyError(null)
    submit.mutate(parsed.data)
  }

  const noteLabel = noteRequired ? 'What needs attention?' : 'Anything you’d like us to know?'
  const countHint = `${String(body.length)} of ${COUPLE_FEEDBACK_BODY_MAX.toLocaleString('en-US')} characters.`
  const noteHint = noteRequired ? countHint : `Optional. ${countHint}`
  // Only promised when there is somewhere to say it.
  const publicly = page.reviewLinks.length > 0 ? ' You can also say it publicly.' : ''

  return (
    <form className="space-y-6" onSubmit={onSubmit} noValidate>
      <div className="grid gap-3">
        <ChoiceCard
          type="radio"
          name="day"
          checked={sentiment === 'needs_attention'}
          onSelect={() => choose('needs_attention')}
          title="Something needs attention"
          description="Tell us what wasn’t right."
        />
        <ChoiceCard
          type="radio"
          name="day"
          checked={sentiment === 'wonderful'}
          onSelect={() => choose('wonderful')}
          title="It was wonderful"
          description={`Tell us what you loved.${publicly}`}
        />
      </div>
      {sentiment === null ? null : (
        <Field
          label={noteLabel}
          htmlFor="feedback-body"
          hint={noteHint}
          error={bodyError}
        >
          <Textarea
            id="feedback-body"
            rows={5}
            maxLength={COUPLE_FEEDBACK_BODY_MAX}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
      )}
      {sentiment === null ? null : (
        <Button type="submit" pending={submit.isPending}>
          Send to {page.venueName}
        </Button>
      )}
      {submit.isError ? <FormError>{requestErrorMessage(submit.error)}</FormError> : null}
    </form>
  )
}

/**
 * How one person in the couple tells the venue about their experience. The
 * venue's review places stay available after they send, and a problem does
 * not ask them to post.
 */
export function CoupleFeedbackPage() {
  const { eventId } = useParams()
  const id = eventId ?? ''
  const query = useCoupleFeedbackPage(id)

  if (query.isPending) {
    return (
      <PageContainer>
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (query.isError) {
    const unavailable = isApiError(query.error) && query.error.status === 404
    return (
      <PageContainer className="space-y-4">
        <ErrorState
          message={unavailable ? 'That event isn’t available.' : requestErrorMessage(query.error)}
          {...(unavailable ? {} : { onRetry: () => { void query.refetch() } })}
        />
        {unavailable ? (
          <div className="flex justify-center">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/events">Back to events</Link>
            </Button>
          </div>
        ) : null}
      </PageContainer>
    )
  }

  const page = query.data
  const answered = page.answer !== null
  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Tell us about your experience"
        meta={`${page.venueName} · ${page.eventName}`}
      />
      {/* Said on the page, not in the header's hover note: it is the promise the note rests on. */}
      <p className="text-sm text-ink">Only {page.venueName} reads this. It isn’t published.</p>
      {answered ? <SentAnswer eventId={id} page={page} /> : <AskForm eventId={id} page={page} />}
      <p className="text-small text-ink-muted">
        <Link to="/terms" className="underline underline-offset-2">
          Terms of service
        </Link>
        {' and '}
        <Link to="/privacy" className="underline underline-offset-2">
          privacy policy
        </Link>
        .
      </p>
    </PageContainer>
  )
}
